import type { ImportPage } from 'services/imports/fileToPages';
import type { OcrTable } from 'services/imports/runOcr';

export type Party = 'borrower' | 'seller' | 'others';
export type Timing = 'at_closing' | 'before_closing' | 'unspecified';
export type Amount = { party: Party; timing: Timing; amount: number };
export type ColumnRole = { party: Party; timing: Timing } | null;

export type ClosingRecord = {
  pageId: string;
  section: string | null;
  subsection: string | null;
  line: string;
  description: string;
  payee: string | null;
  amounts: Amount[];
  table: number;
  row: number;
};

export type TableIssue = { pageId: string; detail: string };

export type PageTables = { page: ImportPage; tables: readonly OcrTable[] };

export type ClosingRecordsResult = {
  records: ClosingRecord[];
  issues: TableIssue[];
};

type Subsection = { letter: string; pageId: string; total: number };
type Context = {
  section: string | null;
  subsection: string | null;
  columns: ColumnRole[] | null;
};
type Accumulator = {
  context: Context;
  records: ClosingRecord[];
  subsections: Map<string, Subsection>;
  totals: Map<string, Subsection>;
  issues: TableIssue[];
};

const SECTIONS = ['LOAN COSTS', 'OTHER COSTS'];
const MONEY_RE = /^\$?-?\$?[\d,]+\.\d{2}$/;
const SUBSECTION_RE = /^([A-J])\.\s+(.+?)\s*$/;
const INLINE_TOTAL_RE = /:\s*(\$?-?\$?[\d,]+\.\d{2})$/;
const LINE_RE = /^(\d{2})(?:\s+(.*))?$/;
const PAYEE_RE = /\sto\s([A-Z][^()]*)$/;
const RECONCILE_TOLERANCE = 0.005;

const parseMoney = (text: string): number | null => {
  const trimmed = text.trim();
  if (!MONEY_RE.test(trimmed)) return null;
  const magnitude = Number(trimmed.replace(/[^\d.]/g, ''));
  return trimmed.includes('-') ? -magnitude : magnitude;
};

const isBlank = (text: string): boolean => text.trim() === '';

const partyOf = (text: string): Party | null => {
  if (text.includes('seller')) return 'seller';
  if (text.includes('borrower')) return 'borrower';
  return null;
};

const partyCarry = (cells: readonly string[]): string[] => cells.reduce<string[]>((carried, cell) => {
  const previous = carried.length > 0 ? carried[carried.length - 1] : '';
  const text = cell.trim().toLowerCase();
  const startsGroup = partyOf(text) !== null || text.includes('others');
  return [...carried, startsGroup ? cell.trim() : previous];
}, []);

const roleFor = (label: string): ColumnRole => {
  const text = label.toLowerCase();
  if (text.includes('others')) return { party: 'others', timing: 'unspecified' };
  const party = partyOf(text);
  if (party === null) return null;
  if (text.includes('before')) return { party, timing: 'before_closing' };
  if (text.includes('closing')) return { party, timing: 'at_closing' };
  return { party, timing: 'unspecified' };
};

const findLineCell = (row: readonly string[]): { index: number; match: RegExpExecArray } | null => {
  const index = row.slice(0, 2).findIndex((cell) => LINE_RE.test(cell.trim()));
  if (index < 0) return null;
  const match = LINE_RE.exec(row[index].trim());
  return match === null ? null : { index, match };
};

type SubsectionHit = { letter: string; title: string; total: number };

const findSubsection = (row: readonly string[]): SubsectionHit | null => {
  const titled = row
    .map((cell) => SUBSECTION_RE.exec(cell.trim()))
    .find((match) => match !== null);
  if (!titled) return null;
  const inline = INLINE_TOTAL_RE.exec(titled[2]);
  const cellTotal = row.map((cell) => parseMoney(cell)).find((value) => value !== null);
  const total = cellTotal ?? (inline ? parseMoney(inline[1]) : null);
  if (total === null || total === undefined) return null;
  return { letter: titled[1], title: titled[2].replace(INLINE_TOTAL_RE, '').trim(), total };
};

const headerRowsOf = (rows: readonly (readonly string[])[]): (readonly string[])[] => {
  const firstData = rows.findIndex((row) => findLineCell(row) !== null || findSubsection(row) !== null);
  return firstData < 0 ? [...rows] : rows.slice(0, firstData);
};

const mapColumns = (headerRows: readonly (readonly string[])[], width: number): ColumnRole[] | null => {
  const carried = headerRows.map((row) => partyCarry(row));
  const roles = Array.from({ length: width }, (_, column) => roleFor(
    headerRows
      .map((row, index) => `${carried[index][column] ?? ''} ${row[column] ?? ''}`)
      .join(' '),
  ));
  return roles.some((role) => role !== null) ? roles : null;
};

const amountsFor = (row: readonly string[], columns: readonly ColumnRole[], skip: number): { amounts: Amount[]; unmapped: number } => {
  const amounts: Amount[] = [];
  let unmapped = 0;
  row.forEach((cell, column) => {
    if (column === skip) return;
    const amount = parseMoney(cell);
    if (amount === null) return;
    const role = columns[column] ?? null;
    if (role === null) {
      unmapped += 1;
      return;
    }
    amounts.push({ ...role, amount });
  });
  return { amounts, unmapped };
};

const isAmountCell = (cell: string, role: ColumnRole): boolean => role !== null && parseMoney(cell) !== null;

const descriptionFor = (row: readonly string[], lineCell: number, leftover: string, columns: readonly ColumnRole[]): string => {
  const parts = row
    .map((cell, column) => {
      if (column === lineCell || isAmountCell(cell, columns[column] ?? null)) return '';
      return cell.trim();
    })
    .filter((cell) => cell !== '');
  return [leftover, ...parts].filter((part) => part !== '').join(' ');
};

const payeeOf = (description: string): string | null => {
  const match = PAYEE_RE.exec(description);
  return match === null ? null : match[1].trim();
};

const processRow = (
  acc: Accumulator,
  row: readonly string[],
  page: ImportPage,
  tableIndex: number,
  rowIndex: number,
): Accumulator => {
  const sectionCell = row.map((cell) => cell.trim()).find((cell) => SECTIONS.includes(cell));
  if (sectionCell !== undefined) {
    return { ...acc, context: { ...acc.context, section: sectionCell, subsection: null } };
  }

  const subsection = findSubsection(row);
  if (subsection !== null) {
    const entry: Subsection = { letter: subsection.letter, pageId: page.id, total: subsection.total };
    const isTotal = subsection.title.toUpperCase().includes('TOTAL');
    if (isTotal) acc.totals.set(subsection.letter, entry);
    else acc.subsections.set(subsection.letter, entry);
    return { ...acc, context: { ...acc.context, subsection: isTotal ? acc.context.subsection : subsection.letter } };
  }

  const lineCell = findLineCell(row);
  if (lineCell === null || acc.context.columns === null) return acc;

  const { columns } = acc.context;
  const { amounts, unmapped } = amountsFor(row, columns, lineCell.index);
  const description = descriptionFor(row, lineCell.index, lineCell.match[2] ?? '', columns);
  if (amounts.length === 0 && description === '' && unmapped === 0) return acc;
  const issues = unmapped > 0
    ? [...acc.issues, { pageId: page.id, detail: `Line ${lineCell.match[1]} has ${unmapped} amount(s) in an unrecognized column` }]
    : acc.issues;
  const record: ClosingRecord = {
    pageId: page.id,
    section: acc.context.section,
    subsection: acc.context.subsection,
    line: lineCell.match[1],
    description,
    payee: payeeOf(description),
    amounts,
    table: tableIndex,
    row: rowIndex,
  };
  return { ...acc, records: [...acc.records, record], issues };
};

const processTable = (acc: Accumulator, page: ImportPage, table: OcrTable, tableIndex: number): Accumulator => {
  const width = table.rows.reduce((max, row) => Math.max(max, row.length), 0);
  const headerColumns = mapColumns(headerRowsOf(table.rows), width);
  const context: Context = {
    ...acc.context,
    columns: headerColumns ?? (acc.context.columns !== null && acc.context.columns.length === width ? acc.context.columns : null),
  };
  return table.rows.reduce(
    (state, row, rowIndex) => processRow(state, row, page, tableIndex, rowIndex),
    { ...acc, context },
  );
};

const sumBorrower = (records: readonly ClosingRecord[], letter: string): number => records
  .filter((record) => record.subsection === letter)
  .reduce((sum, record) => sum + record.amounts
    .filter((amount) => amount.party === 'borrower')
    .reduce((inner, amount) => inner + amount.amount, 0), 0);

const reconcile = (acc: Accumulator): TableIssue[] => {
  const issues: TableIssue[] = [];
  const check = (label: string, pageId: string, expected: number, actual: number): void => {
    if (Math.abs(expected - actual) > RECONCILE_TOLERANCE) {
      issues.push({ pageId, detail: `${label}: printed ${expected.toFixed(2)}, extracted ${actual.toFixed(2)}` });
    }
  };
  acc.subsections.forEach((subsection) => {
    check(`Subsection ${subsection.letter}`, subsection.pageId, subsection.total, sumBorrower(acc.records, subsection.letter));
  });
  const complete = (letters: readonly string[]): boolean => letters.every((letter) => acc.subsections.has(letter));
  const printedSum = (letters: readonly string[]): number => letters.reduce((sum, letter) => sum + (acc.subsections.get(letter)?.total ?? 0), 0);
  const totalD = acc.totals.get('D');
  if (totalD && complete(['A', 'B', 'C'])) check('Total loan costs (D)', totalD.pageId, totalD.total, printedSum(['A', 'B', 'C']));
  const totalI = acc.totals.get('I');
  if (totalI && complete(['E', 'F', 'G', 'H'])) check('Total other costs (I)', totalI.pageId, totalI.total, printedSum(['E', 'F', 'G', 'H']));
  const totalJ = acc.totals.get('J');
  if (totalJ && totalD && totalI) check('Total closing costs (J)', totalJ.pageId, totalJ.total, totalD.total + totalI.total);
  return issues;
};

export const buildClosingRecords = (pages: readonly PageTables[]): ClosingRecordsResult => {
  const initial: Accumulator = {
    context: { section: null, subsection: null, columns: null },
    records: [],
    subsections: new Map(),
    totals: new Map(),
    issues: [],
  };
  const state = pages.reduce((acc, { page, tables }) => tables.reduce(
    (inner, table, tableIndex) => processTable(inner, page, table, tableIndex),
    acc,
  ), initial);
  return {
    records: state.records,
    issues: [...state.issues, ...reconcile(state)],
  };
};
